import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { PageHeader, StageBadge, PriorityBadge, TimelineItem, EmptyState } from '@/components/primitives';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { PlusCircle, LayoutGrid, ListChecks, Users, Trash2, Sparkles, Star, Paperclip, Archive, ArchiveRestore, Pencil } from 'lucide-react';
import { STAGES, STAGE_LABELS, PRIORITY_LABELS, NEXT_ACTIONS, fmtDate } from '@/lib/format';
import AIEmailPanel from '@/components/AIEmailPanel';
import FileUploader from '@/components/FileUploader';

function EditCandidateDialog({ candidate, companyId, onSaved }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(candidate || {});
  const [openJobs, setOpenJobs] = useState([]);

  useEffect(() => {
    if (!open) return;
    setF({
      name: candidate?.name || '',
      email: candidate?.email || '',
      phone: candidate?.phone || '',
      role_applied: candidate?.role_applied || '',
      source: candidate?.source || '',
      job_position_id: candidate?.job_position_id || '',
      expected_ctc: candidate?.expected_ctc ?? '',
      current_ctc: candidate?.current_ctc ?? '',
      experience_years: candidate?.experience_years ?? '',
      notes: candidate?.notes || '',
    });
    api.get(`/jobs?company_id=${companyId}`).then((r) => setOpenJobs(r.data || []));
  }, [open, candidate?.id]);

  const submit = async () => {
    if (!f.name || !f.email) return toast.error('Name and email are required');
    const payload = {
      name: f.name, email: f.email, phone: f.phone || null,
      role_applied: f.role_applied, source: f.source || null,
      job_position_id: f.job_position_id || null,
      expected_ctc: f.expected_ctc === '' ? null : +f.expected_ctc,
      current_ctc: f.current_ctc === '' ? null : +f.current_ctc,
      experience_years: f.experience_years === '' ? null : +f.experience_years,
      notes: f.notes || null,
    };
    try {
      await api.patch(`/candidates/${candidate.id}`, payload);
      toast.success('Candidate updated');
      setOpen(false);
      onSaved && onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" data-testid={`edit-candidate-${candidate.id}`}><Pencil className="h-3.5 w-3.5 mr-1" />Edit details</Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Edit candidate</DialogTitle>
          <DialogDescription>Update personal information, role linkage, CTC and experience. Stage, priority, follow-up and notes are edited inline on the drawer.</DialogDescription>
        </DialogHeader>
        <div className="grid md:grid-cols-2 gap-3">
          <div className="space-y-1.5 md:col-span-2"><Label>Full name *</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="edit-candidate-name" /></div>
          <div className="space-y-1.5"><Label>Email *</Label><Input value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="edit-candidate-email" /></div>
          <div className="space-y-1.5"><Label>Phone</Label><Input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} data-testid="edit-candidate-phone" /></div>
          <div className="space-y-1.5 md:col-span-2">
            <Label>Role (job requirement)</Label>
            <Select value={f.job_position_id || ''} onValueChange={(v) => {
              const j = openJobs.find((x) => x.id === v);
              setF({ ...f, job_position_id: v, role_applied: j?.title || f.role_applied });
            }}>
              <SelectTrigger data-testid="edit-candidate-job"><SelectValue placeholder="Pick a job requirement" /></SelectTrigger>
              <SelectContent>
                {openJobs.map((j) => <SelectItem key={j.id} value={j.id}>{j.title} · {j.status}{j.department ? ` · ${j.department}` : ''}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5"><Label>Role title</Label><Input value={f.role_applied} onChange={(e) => setF({ ...f, role_applied: e.target.value })} /></div>
          <div className="space-y-1.5"><Label>Source</Label><Input value={f.source} onChange={(e) => setF({ ...f, source: e.target.value })} placeholder="LinkedIn, Naukri…" /></div>
          <div className="space-y-1.5"><Label>Expected CTC (₹)</Label><Input type="number" value={f.expected_ctc} onChange={(e) => setF({ ...f, expected_ctc: e.target.value })} data-testid="edit-candidate-ectc" /></div>
          <div className="space-y-1.5"><Label>Current CTC (₹)</Label><Input type="number" value={f.current_ctc} onChange={(e) => setF({ ...f, current_ctc: e.target.value })} /></div>
          <div className="space-y-1.5"><Label>Experience (yrs)</Label><Input type="number" value={f.experience_years} onChange={(e) => setF({ ...f, experience_years: e.target.value })} data-testid="edit-candidate-experience" /></div>
          <div className="space-y-1.5 md:col-span-2"><Label>Notes</Label><Textarea rows={3} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={submit} data-testid="edit-candidate-save">Save changes</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function NewCandidateDialog({ companyId, onCreated }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ name: '', email: '', phone: '', role_applied: '', source: '', stage: 'sourced', priority: 'p2', next_action: 'call', next_followup_date: '', notes: '', job_position_id: '', expected_ctc: '', experience_years: '' });
  const [openJobs, setOpenJobs] = useState([]);

  useEffect(() => {
    if (open) {
      api.get(`/jobs?company_id=${companyId}&status=approved`).then((r) => setOpenJobs(r.data));
    }
  }, [open, companyId]);

  const pickJob = (jid) => {
    const j = openJobs.find((x) => x.id === jid);
    setF({ ...f, job_position_id: jid, role_applied: j?.title || f.role_applied });
  };

  const submit = async () => {
    if (!f.name || !f.email) return toast.error('Name and email are required');
    if (!f.job_position_id) return toast.error('Please pick an open job requirement (Role). If none exists, create it first from Job Requirements tab.');
    try {
      const payload = { ...f, company_id: companyId };
      if (!payload.expected_ctc) delete payload.expected_ctc; else payload.expected_ctc = +payload.expected_ctc;
      if (!payload.experience_years) delete payload.experience_years; else payload.experience_years = +payload.experience_years;
      if (!payload.next_followup_date) delete payload.next_followup_date;
      await api.post('/candidates', payload);
      toast.success('Candidate added — auto-rating started in background.'); setOpen(false); setF({ name: '', email: '', phone: '', role_applied: '', source: '', stage: 'sourced', priority: 'p2', next_action: 'call', next_followup_date: '', notes: '', job_position_id: '', expected_ctc: '', experience_years: '' });
      onCreated && onCreated();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid="add-candidate-button"><PlusCircle className="h-4 w-4 mr-2" />Add candidate</Button></DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader><DialogTitle>Add candidate</DialogTitle><DialogDescription>Basic information; you can enrich after creating.</DialogDescription></DialogHeader>
        <div className="grid md:grid-cols-2 gap-3">
          <div className="space-y-1.5 md:col-span-2"><Label>Full name *</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="candidate-name-input" /></div>
          <div className="space-y-1.5"><Label>Email *</Label><Input value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="candidate-email-input" /></div>
          <div className="space-y-1.5"><Label>Phone</Label><Input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} /></div>
          <div className="space-y-1.5 md:col-span-2">
            <Label>Role (open job requirement) *</Label>
            {openJobs.length === 0 ? (
              <div className="rounded-md border border-dashed p-3 text-sm text-muted-foreground">
                No open job requirements. Please create + approve a Job Requirement first from the <span className="font-medium text-foreground">Job Requirements</span> tab.
              </div>
            ) : (
              <Select value={f.job_position_id || ''} onValueChange={(v) => pickJob(v)}>
                <SelectTrigger data-testid="candidate-job-select"><SelectValue placeholder="Pick a role from open job requirements" /></SelectTrigger>
                <SelectContent>
                  {openJobs.map((j) => <SelectItem key={j.id} value={j.id}>{j.title} · {j.positions_filled || 0}/{j.positions_count} filled{j.department ? ` · ${j.department}` : ''}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
            <p className="text-[11px] text-muted-foreground">Only APPROVED and OPEN job requirements show. AI will auto-rate this candidate against the selected role.</p>
          </div>
          <div className="space-y-1.5"><Label>Expected CTC (₹)</Label><Input type="number" value={f.expected_ctc} onChange={(e) => setF({ ...f, expected_ctc: e.target.value })} /></div>
          <div className="space-y-1.5"><Label>Experience (yrs)</Label><Input type="number" value={f.experience_years} onChange={(e) => setF({ ...f, experience_years: e.target.value })} /></div>
          <div className="space-y-1.5"><Label>Source</Label><Input value={f.source} onChange={(e) => setF({ ...f, source: e.target.value })} placeholder="LinkedIn, Naukri…" /></div>
          <div className="space-y-1.5"><Label>Stage</Label><Select value={f.stage} onValueChange={(v) => setF({ ...f, stage: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{STAGES.map((s) => <SelectItem key={s} value={s}>{STAGE_LABELS[s]}</SelectItem>)}</SelectContent></Select></div>
          <div className="space-y-1.5"><Label>Priority</Label><Select value={f.priority} onValueChange={(v) => setF({ ...f, priority: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{Object.keys(PRIORITY_LABELS).map((p) => <SelectItem key={p} value={p}>{PRIORITY_LABELS[p]}</SelectItem>)}</SelectContent></Select></div>
          <div className="space-y-1.5"><Label>Next action</Label><Select value={f.next_action} onValueChange={(v) => setF({ ...f, next_action: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{NEXT_ACTIONS.map((a) => <SelectItem key={a} value={a} className="capitalize">{a}</SelectItem>)}</SelectContent></Select></div>
          <div className="space-y-1.5"><Label>Follow-up date</Label><Input type="date" value={f.next_followup_date} onChange={(e) => setF({ ...f, next_followup_date: e.target.value })} data-testid="candidate-followup-date" /></div>
          <div className="space-y-1.5 md:col-span-2"><Label>Notes</Label><Textarea rows={3} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} /></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={submit} data-testid="candidate-save-button">Save candidate</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CandidateDrawer({ id, companyId, open, onOpenChange, onChanged }) {
  const [candidate, setCandidate] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [files, setFiles] = useState([]);
  const [rating, setRating] = useState(false);

  useEffect(() => {
    if (!id || !open) return;
    api.get(`/candidates/${id}`).then((r) => setCandidate(r.data));
    api.get(`/templates?company_id=${companyId}&category=recruitment`).then((r) => setTemplates(r.data));
    api.get(`/files?entity_type=candidate&entity_id=${id}`).then((r) => setFiles(r.data));
  }, [id, open]);

  const patch = async (fields) => {
    try { const r = await api.patch(`/candidates/${id}`, fields); setCandidate(r.data); onChanged && onChanged(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Update failed'); }
  };

  const addTimelineEvent = async (kind, title) => {
    try { await api.post(`/candidates/${id}/timeline`, { kind, title }); const r = await api.get(`/candidates/${id}`); setCandidate(r.data); toast.success('Logged'); }
    catch (e) { toast.error('Failed'); }
  };

  const rateNow = async () => {
    if (!candidate?.job_position_id) return toast.error('Link this candidate to a job requirement first.');
    setRating(true);
    try {
      const r = await api.post('/ai/rate-candidate', { candidate_id: id, job_id: candidate.job_position_id });
      await patch({});
      const cur = await api.get(`/candidates/${id}`); setCandidate(cur.data);
      toast.success(`Rated: ${r.data.combined_score}/100`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Rating failed'); }
    finally { setRating(false); }
  };

  if (!candidate) return null;
  const isArchived = !!candidate.archived;
  const toggleArchive = async () => {
    try {
      await api.patch(`/candidates/${id}`, { archived: !isArchived });
      const r = await api.get(`/candidates/${id}`);
      setCandidate(r.data);
      toast.success(isArchived ? 'Candidate restored to active list' : 'Candidate archived');
      onChanged && onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-3xl overflow-y-auto" data-testid="candidate-drawer">
        <SheetHeader>
          <SheetTitle className="font-display flex items-center gap-2 flex-wrap">
            {candidate.name}
            {isArchived && <Badge variant="outline" className="text-[11px]">Archived · {candidate.archived_reason || 'manual'}</Badge>}
          </SheetTitle>
          <SheetDescription>{candidate.role_applied} · {candidate.email}</SheetDescription>
          <div className="flex items-center gap-2 pt-2">
            <EditCandidateDialog candidate={candidate} companyId={companyId} onSaved={async () => { const r = await api.get(`/candidates/${id}`); setCandidate(r.data); onChanged && onChanged(); }} />
            <Button size="sm" variant="outline" onClick={toggleArchive} data-testid={isArchived ? 'restore-candidate-btn' : 'archive-candidate-btn'}>
              {isArchived ? <><ArchiveRestore className="h-3.5 w-3.5 mr-1" />Restore</> : <><Archive className="h-3.5 w-3.5 mr-1" />Archive</>}
            </Button>
          </div>
        </SheetHeader>

        <div className="mt-4 grid gap-4 lg:grid-cols-2">
          <div className="space-y-4">
            <div className="rounded-xl border bg-card p-4 space-y-3">
              <div className="flex items-center gap-2 flex-wrap">
                <StageBadge stage={candidate.stage} />
                <PriorityBadge priority={candidate.priority} />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1">
                  <Label className="text-xs">Stage</Label>
                  <Select value={candidate.stage} onValueChange={(v) => patch({ stage: v })}>
                    <SelectTrigger data-testid="drawer-stage-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{STAGES.map((s) => <SelectItem key={s} value={s}>{STAGE_LABELS[s]}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Priority</Label>
                  <Select value={candidate.priority} onValueChange={(v) => patch({ priority: v })}>
                    <SelectTrigger data-testid="drawer-priority-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{Object.keys(PRIORITY_LABELS).map((p) => <SelectItem key={p} value={p}>{PRIORITY_LABELS[p]}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Next action</Label>
                  <Select value={candidate.next_action || 'call'} onValueChange={(v) => patch({ next_action: v })}>
                    <SelectTrigger data-testid="drawer-next-action"><SelectValue /></SelectTrigger>
                    <SelectContent>{NEXT_ACTIONS.map((a) => <SelectItem key={a} value={a} className="capitalize">{a}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Follow-up date</Label>
                  <Input type="date" value={candidate.next_followup_date || ''} onChange={(e) => patch({ next_followup_date: e.target.value })} data-testid="drawer-followup-date" />
                </div>
              </div>
              <Textarea rows={3} placeholder="Notes…" value={candidate.notes || ''} onChange={(e) => setCandidate({ ...candidate, notes: e.target.value })} onBlur={(e) => patch({ notes: e.target.value })} />
              <div className="flex flex-wrap gap-2">
                <Button variant="outline" size="sm" onClick={() => addTimelineEvent('call', 'Called candidate')} data-testid="log-call-button">Log call</Button>
                <Button variant="outline" size="sm" onClick={() => addTimelineEvent('interview', 'Interview conducted')} data-testid="log-interview-button">Log interview</Button>
                <Button variant="outline" size="sm" onClick={() => addTimelineEvent('note', prompt('Note:') || 'Note')} data-testid="log-note-button">Add note</Button>
              </div>
            </div>

            <div className="rounded-xl border bg-card p-4 space-y-3">
              <div className="flex items-center justify-between">
                <h4 className="font-display font-semibold flex items-center gap-2"><Paperclip className="h-4 w-4 text-primary" /> Resume & documents</h4>
                {candidate.ai_rating && <div className="flex items-center gap-1 text-sm"><Star className="h-4 w-4 text-primary" /><span className="tabular-nums font-medium">{candidate.ai_rating}/100</span></div>}
              </div>
              <FileUploader entityType="candidate" entityId={candidate.id} label="resume" files={files} onUploaded={(f) => setFiles([f, ...files])} onDeleted={(fid) => setFiles(files.filter((x) => x.id !== fid))} buttonText="Upload resume/PDF" />
              {candidate.job_position_id && (
                <div className="pt-2 border-t space-y-2">
                  <div className="flex items-center justify-between">
                    <p className="text-xs text-muted-foreground">Linked to a job requirement — rate this candidate:</p>
                    <Button size="sm" variant="outline" onClick={rateNow} disabled={rating} data-testid="rate-candidate-btn">{rating ? 'Rating…' : <><Sparkles className="h-3.5 w-3.5 mr-1" />AI rate fit</>}</Button>
                  </div>
                  {candidate.rating_detail && (
                    <div className="rounded-lg bg-muted/40 p-3 text-xs space-y-1">
                      <p><span className="font-semibold">Combined:</span> {candidate.rating_detail.combined_score}/100 · AI: {candidate.rating_detail.ai_score} · Keyword: {candidate.rating_detail.keyword_score}%</p>
                      <p><span className="font-semibold">Why:</span> {candidate.rating_detail.ai_reasoning}</p>
                      {candidate.rating_detail.strengths?.length > 0 && <p><span className="font-semibold">Strengths:</span> {candidate.rating_detail.strengths.join('; ')}</p>}
                      {candidate.rating_detail.gaps?.length > 0 && <p><span className="font-semibold">Gaps:</span> {candidate.rating_detail.gaps.join('; ')}</p>}
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="rounded-xl border bg-card p-4">
              <h4 className="font-display font-semibold mb-3">Timeline</h4>
              <div className="relative timeline-rail pl-1">
                {(candidate.timeline || []).slice().reverse().map((ev) => (
                  <TimelineItem key={ev.id} event={ev} />
                ))}
                {(!candidate.timeline || candidate.timeline.length === 0) && <p className="text-sm text-muted-foreground">No activity yet.</p>}
              </div>
            </div>
          </div>
          <div>
            <AIEmailPanel
              candidate={{ ...candidate, company_name: candidate.company_name }}
              companyId={companyId}
              entityType="candidate"
              entityId={candidate.id}
              templates={templates}
              onSent={() => api.get(`/candidates/${id}`).then((r) => setCandidate(r.data))}
            />
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

function KanbanBoard({ candidates, onOpen, onStageChange }) {
  const [dragging, setDragging] = useState(null);

  const dropOn = (stage) => (e) => {
    e.preventDefault();
    if (!dragging || dragging.stage === stage) return;
    onStageChange(dragging.id, stage);
    setDragging(null);
  };

  return (
    <div className="kanban-scroll flex gap-3 overflow-x-auto pb-3">
      {STAGES.map((s) => {
        const items = candidates.filter((c) => c.stage === s);
        return (
          <div key={s} className="min-w-[260px] w-[260px] rounded-xl border bg-muted/40 p-2" onDragOver={(e) => e.preventDefault()} onDrop={dropOn(s)} data-testid={`kanban-column-${s}`}>
            <div className="flex items-center justify-between px-2 py-1">
              <div className="flex items-center gap-2"><StageBadge stage={s} /><span className="text-xs text-muted-foreground">{items.length}</span></div>
            </div>
            <div className="space-y-2 mt-2">
              {items.map((c) => (
                <div
                  key={c.id}
                  draggable
                  onDragStart={() => setDragging({ id: c.id, stage: c.stage })}
                  onClick={() => onOpen(c.id)}
                  className="rounded-lg border bg-card p-3 hover:shadow cursor-grab active:cursor-grabbing"
                  data-testid={`kanban-card-${c.id}`}
                >
                  <p className="font-medium truncate">{c.name}</p>
                  <p className="text-xs text-muted-foreground truncate">{c.role_applied}</p>
                  <div className="flex items-center justify-between mt-2">
                    <PriorityBadge priority={c.priority} />
                    <span className="text-[10px] text-muted-foreground">{c.next_followup_date ? fmtDate(c.next_followup_date) : ''}</span>
                  </div>
                </div>
              ))}
              {items.length === 0 && <p className="text-xs text-muted-foreground px-2 py-6 text-center">Empty</p>}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function RecruitmentPage() {
  const { companyId } = useParams();
  const [company, setCompany] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [view, setView] = useState('kanban');
  const [scope, setScope] = useState('active'); // active | archived | all
  const [search, setSearch] = useState('');
  const [priority, setPriority] = useState('all');
  const [openId, setOpenId] = useState(null);
  const [params, setParams] = useSearchParams();

  const load = async () => {
    const qs = new URLSearchParams({ company_id: companyId, view: scope });
    if (search) qs.set('search', search);
    if (priority !== 'all') qs.set('priority', priority);
    const r = await api.get(`/candidates?${qs.toString()}`);
    setCandidates(r.data);
  };
  useEffect(() => { api.get(`/companies/${companyId}`).then((r) => setCompany(r.data)); }, [companyId]);
  useEffect(() => { load(); }, [companyId, search, priority, scope]);
  useEffect(() => {
    const q = params.get('open');
    if (q) { setOpenId(q); params.delete('open'); setParams(params); }
  }, [params]);

  const changeStage = async (id, stage) => {
    try {
      await api.patch(`/candidates/${id}`, { stage });
      toast.success(`Moved to ${STAGE_LABELS[stage]}`);
      load();
    } catch (e) { toast.error('Move failed'); }
  };

  const del = async (id) => {
    if (!window.confirm('Delete candidate?')) return;
    try { await api.delete(`/candidates/${id}`); toast.success('Deleted'); load(); } catch (e) { toast.error('Delete failed'); }
  };

  const archiveInline = async (id, archived) => {
    try {
      await api.patch(`/candidates/${id}`, { archived });
      toast.success(archived ? 'Archived' : 'Restored to active');
      load();
    } catch (e) { toast.error('Failed'); }
  };

  const isArchivedView = scope === 'archived';
  const showKanban = !isArchivedView && view === 'kanban';

  return (
    <>
      <PageHeader
        title={`${company?.name || '…'} · Recruitment`}
        description="Move candidates through stages, capture activity, draft AI-powered emails with CC to directors and managers. Closed job requirements archive their candidates automatically."
        actions={<NewCandidateDialog companyId={companyId} onCreated={load} />}
      >
        <div className="flex items-center gap-2 flex-wrap">
          <Tabs value={scope} onValueChange={setScope}>
            <TabsList data-testid="recruitment-scope">
              <TabsTrigger value="active" data-testid="scope-active">Active</TabsTrigger>
              <TabsTrigger value="archived" data-testid="scope-archived"><Archive className="h-3.5 w-3.5 mr-1" />Archived</TabsTrigger>
              <TabsTrigger value="all" data-testid="scope-all">All</TabsTrigger>
            </TabsList>
          </Tabs>
          {!isArchivedView && (
            <Tabs value={view} onValueChange={setView}>
              <TabsList data-testid="recruitment-view-toggle">
                <TabsTrigger value="kanban"><LayoutGrid className="h-3.5 w-3.5 mr-1" />Kanban</TabsTrigger>
                <TabsTrigger value="list"><ListChecks className="h-3.5 w-3.5 mr-1" />List</TabsTrigger>
              </TabsList>
            </Tabs>
          )}
          <Input placeholder="Search candidates…" value={search} onChange={(e) => setSearch(e.target.value)} className="h-9 w-56" data-testid="recruitment-search" />
          <Select value={priority} onValueChange={setPriority}>
            <SelectTrigger className="h-9 w-40" data-testid="recruitment-priority-filter"><SelectValue placeholder="Priority" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All priorities</SelectItem>
              {Object.keys(PRIORITY_LABELS).map((p) => <SelectItem key={p} value={p}>{PRIORITY_LABELS[p]}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </PageHeader>

      <div className="px-4 sm:px-6 lg:px-8 py-6">
        {candidates.length === 0 ? (
          <EmptyState
            icon={isArchivedView ? Archive : Users}
            title={isArchivedView ? 'No archived candidates' : 'No candidates yet'}
            description={isArchivedView ? 'Candidates linked to closed job requirements are stored here for future use.' : 'Add your first candidate. You can move them through the pipeline with drag & drop.'}
            action={!isArchivedView ? <NewCandidateDialog companyId={companyId} onCreated={load} /> : null}
          />
        ) : showKanban ? (
          <KanbanBoard candidates={candidates} onOpen={(id) => setOpenId(id)} onStageChange={changeStage} />
        ) : (
          <div className="rounded-xl border bg-card overflow-hidden">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Candidate</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Stage</TableHead>
                <TableHead>Priority</TableHead>
                <TableHead>Next</TableHead>
                <TableHead>Follow-up</TableHead>
                {(scope !== 'active') && <TableHead>Status</TableHead>}
                <TableHead></TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {candidates.map((c) => (
                  <TableRow key={c.id} className="cursor-pointer" onClick={() => setOpenId(c.id)} data-testid={`candidate-row-${c.id}`}>
                    <TableCell><p className="font-medium">{c.name}</p><p className="text-xs text-muted-foreground">{c.email}</p></TableCell>
                    <TableCell>{c.role_applied}</TableCell>
                    <TableCell><StageBadge stage={c.stage} /></TableCell>
                    <TableCell><PriorityBadge priority={c.priority} /></TableCell>
                    <TableCell className="capitalize text-sm">{c.next_action || '—'}</TableCell>
                    <TableCell>{fmtDate(c.next_followup_date)}</TableCell>
                    {(scope !== 'active') && (
                      <TableCell>
                        {c.archived ? (
                          <Badge variant="outline" className="text-[11px]">Archived</Badge>
                        ) : (
                          <Badge variant="secondary" className="text-[11px]">Active</Badge>
                        )}
                      </TableCell>
                    )}
                    <TableCell>
                      <div className="flex items-center justify-end gap-1">
                        {c.archived ? (
                          <Button size="icon" variant="ghost" onClick={(e) => { e.stopPropagation(); archiveInline(c.id, false); }} data-testid={`restore-candidate-inline-${c.id}`} title="Restore">
                            <ArchiveRestore className="h-4 w-4 text-muted-foreground" />
                          </Button>
                        ) : (
                          <Button size="icon" variant="ghost" onClick={(e) => { e.stopPropagation(); archiveInline(c.id, true); }} data-testid={`archive-candidate-inline-${c.id}`} title="Archive">
                            <Archive className="h-4 w-4 text-muted-foreground" />
                          </Button>
                        )}
                        <Button size="icon" variant="ghost" onClick={(e) => { e.stopPropagation(); del(c.id); }} data-testid={`candidate-delete-${c.id}`}><Trash2 className="h-4 w-4 text-muted-foreground" /></Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      <CandidateDrawer id={openId} companyId={companyId} open={!!openId} onOpenChange={(o) => !o && setOpenId(null)} onChanged={load} />
    </>
  );
}
